import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GameChallengePopup } from '@/components/GameChallengePopup';
import type { Game, GameChallenge, PlayerProgress } from '@/types/game';
import type { PlayerStats } from '@/components/GameChallengePopup/types';

const resolveImageUrl = (imageUrl: string, frontendUrl: string): string =>
  imageUrl.startsWith('/') ? `${frontendUrl}${imageUrl}` : imageUrl;

interface WalkthroughStep {
  imageUrl: string;
  caption: string;
}

interface WalkthroughData {
  steps: WalkthroughStep[];
}

export interface FallbackContext {
  challenge: GameChallenge;
  playerStats: PlayerStats | null;
  progress?: PlayerProgress;
  isCurrentPeriod: boolean;
  onPlay: () => void;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onArchivesClick?: () => void;
}

export interface GameWalkthroughModalProps {
  game: Game;
  playLabel?: string;
  isOpen: boolean;
  onClose: () => void;
  fallbackContext: FallbackContext;
}

export function GameWalkthroughModal({
  game,
  playLabel = "Play Today's Challenge",
  isOpen,
  onClose,
  fallbackContext,
}: GameWalkthroughModalProps) {
  const [steps, setSteps] = useState<WalkthroughStep[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [fallback, setFallback] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Reset carousel state on each open
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setFallback(false);
      setIsLoading(true);
      setSteps([]);
    }
  }, [isOpen]);

  // Fetch steps.json from game frontend origin (IS-INV-042)
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const url = `${game.frontendUrl}/how-to-play/steps.json`;
        const res = await fetch(url, { credentials: 'omit' }); // OWASP A5: no creds to game origin
        if (!res.ok) throw new Error(`non-200: ${res.status}`);
        const data = (await res.json()) as WalkthroughData;
        if (!Array.isArray(data.steps) || data.steps.length === 0) throw new Error('empty');
        if (!cancelled) {
          setSteps(data.steps);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setFallback(true); // IS-INV-042: all failures render GameChallengePopup
          setIsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, game.frontendUrl]);

  if (!isOpen) return null;

  // IS-INV-042: any fetch failure (network, non-200, malformed JSON, empty steps[]) → GameChallengePopup
  if (fallback) {
    return (
      <GameChallengePopup
        game={game}
        challenge={fallbackContext.challenge}
        playerStats={fallbackContext.playerStats}
        progress={fallbackContext.progress}
        isCurrentPeriod={fallbackContext.isCurrentPeriod}
        onPlay={fallbackContext.onPlay}
        onClose={onClose}
        isLoading={fallbackContext.isLoading}
        error={fallbackContext.error ?? null}
        onRetry={fallbackContext.onRetry}
        onArchivesClick={fallbackContext.onArchivesClick}
      />
    );
  }

  const totalSteps = steps.length;
  const isLastStep = currentStep === totalSteps - 1;
  const step = steps[currentStep];

  return (
    <Dialog.Root open={true} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/50',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 w-[calc(100%-2rem)] max-w-lg',
            'translate-x-[-50%] translate-y-[-50%]',
            'bg-surface rounded-xl shadow-modal overflow-hidden',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
          )}
          style={{ '--signature-color': game.signatureColor } as React.CSSProperties}
          aria-describedby={undefined}
        >
          {/* Branded header — IS-INV-009: CSS custom property set on parent, never hardcoded hex */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ background: 'var(--signature-color)' }}
          >
            <Dialog.Title className="font-display text-white text-lg leading-none">
              How to Play
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="text-white/80 hover:text-white transition-colors focus:outline-none"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <div className="p-4 md:p-6">
            {isLoading ? (
              /* Loading state — fixed height matches image container to prevent layout shift */
              <div className="flex items-center justify-center h-[170px] md:h-[220px]">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
              </div>
            ) : step ? (
              <>
                {/* Image container — fixed height: 170px mobile / 220px desktop (AC-11, IS-INV-013) */}
                <div className="w-full h-[170px] md:h-[220px] mb-4 overflow-hidden rounded-lg bg-surface-alt">
                  <img
                    src={resolveImageUrl(step.imageUrl, game.frontendUrl ?? '')}
                    alt={`Step ${currentStep + 1} of ${totalSteps}`}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Caption — IS-INV-041: DOMPurify with strict ALLOWED_TAGS allowlist */}
                <p
                  className="text-sm md:text-base text-foreground text-center text-balance mb-4 min-h-[2.5rem]"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(step.caption, { ALLOWED_TAGS: ['strong'] }),
                  }}
                />

                {/* Progress dots + "Step X of Y" label */}
                <div className="flex flex-col items-center gap-2 mb-5">
                  <span className="text-xs text-muted-foreground font-emphasis">
                    Step {currentStep + 1} of {totalSteps}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {steps.map((_, i) => (
                      <span
                        key={i}
                        className={cn(
                          'rounded-full transition-all duration-200',
                          i === currentStep
                            ? 'h-2 w-[22px] bg-foreground'   // active: pill ~22px
                            : 'h-2 w-2 bg-muted'              // inactive: circle ~7px
                        )}
                      />
                    ))}
                  </div>
                </div>

                {/* Navigation row */}
                <div className="flex items-center gap-2">
                  {/* Prev — small circle icon, invisible on step 1 (AC-10) */}
                  <button
                    onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
                    className={cn(
                      'h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full',
                      'border border-border text-foreground hover:bg-surface-alt transition-colors',
                      currentStep === 0 && 'invisible'
                    )}
                    aria-label="Previous step"
                    tabIndex={currentStep === 0 ? -1 : 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {/* Next — flex-1, outline, fills remaining space (AC-10) */}
                  {!isLastStep && (
                    <button
                      onClick={() => setCurrentStep((s) => s + 1)}
                      className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-sm font-emphasis border-2 border-foreground text-foreground hover:bg-surface-alt transition-colors"
                      aria-label="Next step"
                    >
                      Next <ChevronRight className="h-4 w-4" />
                    </button>
                  )}

                  {/* Play — always visible, outline on intermediate / filled on last (AC-10, AC-14) */}
                  <button
                    onClick={() => fallbackContext.onPlay()}
                    className={cn(
                      'rounded-lg text-sm font-emphasis font-medium transition-opacity hover:opacity-90',
                      isLastStep
                        ? 'flex-1 px-4 py-2 text-white'
                        : 'px-4 py-2 border-2 bg-transparent'
                    )}
                    style={
                      isLastStep
                        ? { background: 'var(--signature-color)' }
                        : { borderColor: 'var(--signature-color)', color: 'var(--signature-color)' }
                    }
                  >
                    {playLabel}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
